import React, { useState } from "react";
import toast from "react-hot-toast";
import { RxCross2 } from "react-icons/rx";
import apiClient from "../../services/apiClient";
import { useSelector } from "react-redux";

const ReviewFormPopup = ({ onClose, order }) => {
  const [rating, setRating] = useState(0);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewDesc, setReviewDesc] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useSelector((state) => state.auth);

  // Get unique products from order
  const getProducts = () => {
    if (!order || !order.order_items) return [];
    
    const productsMap = new Map();
    
    order.order_items.forEach(item => {
      if (item.product_type === 'combo' && item.combo) {
        productsMap.set(item.combo.id, {
          id: item.combo.id,
          name: item.combo.title,
          type: 'combo'
        });
      } else if (item.product) {
        productsMap.set(item.product.id, {
          id: item.product.id,
          name: item.product.name,
          type: 'normal'
        });
      }
    });
    
    return Array.from(productsMap.values());
  };

  const products = getProducts();
  
  // Create a map for easy product type lookup
  const productTypeMap = {};
  products.forEach(product => {
    productTypeMap[product.id] = product.type;
  });

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedProductId) {
      toast.error("Please select a product to review");
      return;
    }
    
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    
    if (!reviewTitle.trim()) {
      toast.error("Please enter a review title");
      return;
    }
    
    if (!reviewDesc.trim()) {
      toast.error("Please enter a review description");
      return;
    }

    try {
      setSubmitting(true);
      
      const formData = new FormData();
      formData.append("rating", rating);
      formData.append("title", reviewTitle);
      formData.append("comment", reviewDesc);
      formData.append("product_id", selectedProductId);
      formData.append("product_type", productTypeMap[selectedProductId]);
      formData.append("user_id", user?.id || "");
      formData.append("order_id", order?.id || "");

      // Append images if any
      selectedImages.forEach((file, index) => {
        formData.append(`images[${index}]`, file);
      });
      
      // Make API call
      await apiClient.post("/reviews", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // Show success message
      toast.success("Review submitted successfully!");
      
      onClose();
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    // Validate file types and size if needed
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024 // 5MB limit
    );
    
    if (validFiles.length !== files.length) {
      toast.error("Some files were not valid images or were too large (max 5MB)");
    }
    
    setSelectedImages((prev) => [...prev, ...validFiles]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="ReviewPopupOverlay">
      <div className="ReviewPopupBox">
        <button className="ClosePopupBtn" onClick={onClose}>
          <RxCross2 />
        </button>

        <h2>Write a Review</h2>

        <form className="ReviewForm" onSubmit={handleSubmitReview}>
          {/* Product Selection */}
          {products.length > 0 && (
            <div className="FormField">
              <label>
                Select Product<span className="Required">*</span>
              </label>
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                required
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Star Rating */}
          <div className="FormField">
            <label>
              Your Rating<span className="Required">*</span>
            </label>
            <div className="StarRating">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`Star ${rating >= star ? "filled" : ""}`}
                  onClick={() => setRating(star)}
                >
                  ★
                </span>
              ))}
            </div>
          </div>

          {/* Review Title */}
          <div className="FormField">
            <label>
              Review Title<span className="Required">*</span>
            </label>
            <input
              type="text"
              value={reviewTitle}
              onChange={(e) => setReviewTitle(e.target.value)}
              placeholder="Enter a title for your review"
              required
            />
          </div>

          {/* Review Description */}
          <div className="FormField">
            <label>
              Review Description<span className="Required">*</span>
            </label>
            <textarea
              value={reviewDesc}
              onChange={(e) => setReviewDesc(e.target.value)}
              placeholder="Share your experience with this product"
              rows="4"
              required
            />
          </div>

          {/* Image Upload */}
          <div className="FormField">
            <label>Add Photos (Optional)</label>
            <div
              className="AddPhotoBox"
              onClick={() => document.getElementById("reviewImages").click()}
            >
              <span className="PlusIcon">+</span>
              <p>Add Photos</p>
            </div>
            <input
              id="reviewImages"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />

            {selectedImages.length > 0 && (
              <div className="ImagePreviewGrid">
                {selectedImages.map((img, index) => (
                  <div className="ImagePreview" key={index}>
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`Preview-${index}`}
                    />
                    <button
                      type="button"
                      className="RemoveImage"
                      onClick={() => handleRemoveImage(index)}
                    >
                      ✖
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ButtonGroup">
            <button 
              type="submit" 
              className="SubmitReviewBtn"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewFormPopup;